import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { collection, deleteDoc, doc, getDocs, setDoc, Timestamp } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../../Pages/dash.css';
import './statutoryRemittances.css';
import {
  compareMonthKeys,
  formatKes,
  formatShortDate,
  getDateInputValue,
  getDateTimeInputValue,
  getMonthKey,
  getMonthLabel,
  normalizeAmount,
  toDateValue
} from '../../utils/financeWorkflow';
import { logAdminActivity } from '../../utils/activityLogger';

const LOTUS_API_BASE_URL = process.env.REACT_APP_LOTUS_API_BASE_URL
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/lotusApi';
const LOTUS_RECONCILE_INTERVAL_MS = 15 * 60 * 1000;

const STATUTORY_TYPE_OPTIONS = ['PAYE', 'NSSF', 'SHA', 'Housing Levy', 'Other'];
const PAYMENT_METHOD_OPTIONS = ['Bank Transfer', 'MPESA', 'Paybill', 'Till Number'];

const PAYMENT_METHOD_TO_LOTUS = {
  'Bank Transfer': 'pesalink',
  MPESA: 'mpesa',
  Paybill: 'paybill',
  'Till Number': 'till'
};

const normalizeText = (value) => String(value || '').trim();

const normalizePaymentStatus = (value) => normalizeText(value || 'Pending').toLowerCase();

const isSuccessfulLotusStatus = (status) => {
  const normalized = normalizePaymentStatus(status);
  return ['paid', 'processed', 'completed', 'successful', 'success', 'succeeded', 'approved'].includes(normalized);
};

const isFailedLotusStatus = (status) => {
  const normalized = normalizePaymentStatus(status);
  return ['failed', 'failure', 'rejected', 'declined', 'cancelled', 'canceled', 'error'].includes(normalized);
};

const normalizeLotusTransactionId = (value) => normalizeText(value).replace(/^TR[-_ ]*/i, '');

const findNestedValue = (source, keys) => {
  const queue = [source];
  const seen = new Set();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || seen.has(current)) {
      continue;
    }

    seen.add(current);

    for (const key of keys) {
      const value = current[key];
      if (value !== undefined && value !== null && normalizeText(value) !== '') {
        return value;
      }
    }

    for (const value of Object.values(current)) {
      if (value && typeof value === 'object') {
        queue.push(value);
      }
    }
  }

  return '';
};

const extractLotusPaymentDetails = (payload) => {
  const source = payload?.data?.transaction
    || payload?.data
    || payload?.transaction
    || payload?.payment
    || payload?.result
    || payload
    || {};

  return {
    lotusTransactionId: normalizeLotusTransactionId(findNestedValue(source, [
      'transaction_id',
      'transactionId',
      'payment_id',
      'paymentId',
      'id'
    ])),
    lotusTransactionStatus: normalizeText(findNestedValue(source, [
      'status',
      'payment_status',
      'paymentStatus',
      'state',
      'result',
      'transaction_status',
      'transactionStatus'
    ])),
    lotusTransactionMessage: normalizeText(findNestedValue(payload, [
      'message',
      'detail',
      'description',
      'response_message',
      'responseMessage'
    ]))
  };
};

const getDefaultRecipientName = (type) => {
  if (type === 'PAYE' || type === 'Housing Levy') {
    return 'KRA';
  }

  if (type === 'NSSF') {
    return 'NSSF';
  }

  if (type === 'SHA') {
    return 'SHA';
  }

  return 'Statutory body';
};

const getDestinationSummary = (item = {}) => {
  const method = normalizeText(item.paymentMethod);

  if (method === 'Bank Transfer') {
    return [
      item.bankName || 'Bank not set',
      item.bankAccountNumber || 'Account not set'
    ].join(' • ');
  }

  if (method === 'MPESA') {
    return item.phoneNumber || 'Phone not set';
  }

  if (method === 'Paybill') {
    return [
      item.paybillNumber || 'Paybill not set',
      item.paybillAccountNumber || 'Account not set'
    ].join(' • ');
  }

  if (method === 'Till Number') {
    return item.tillNumber || 'Till number not set';
  }

  return 'Payment method not set';
};

const getStatusTone = (status) => {
  const normalized = normalizePaymentStatus(status);

  if (normalized === 'paid') {
    return 'success';
  }

  if (normalized === 'partially paid' || normalized === 'pending' || normalized === 'submitted') {
    return 'warning';
  }

  if (normalized === 'overdue' || normalized === 'failed') {
    return 'danger';
  }

  return 'secondary';
};

const isPaidPaymentStatus = (status) => normalizePaymentStatus(status) === 'paid';

const buildEmptyRemittanceForm = (defaults = {}) => ({
  remittanceType: defaults.remittanceType || 'PAYE',
  periodKey: defaults.periodKey || getMonthKey(),
  amount: defaults.amount || '',
  dueDate: defaults.dueDate || getDateInputValue(),
  paymentMethod: defaults.paymentMethod || 'Bank Transfer',
  recipientName: defaults.recipientName || getDefaultRecipientName(defaults.remittanceType || 'PAYE'),
  authorityReference: defaults.authorityReference || '',
  bankId: defaults.bankId || '',
  bankName: defaults.bankName || '',
  bankCode: defaults.bankCode || '',
  bankAccountNumber: defaults.bankAccountNumber || '',
  phoneNumber: defaults.phoneNumber || '',
  paybillNumber: defaults.paybillNumber || '',
  paybillAccountNumber: defaults.paybillAccountNumber || '',
  tillNumber: defaults.tillNumber || '',
  notes: defaults.notes || ''
});

const buildEmptyPaymentForm = (remittance = null) => ({
  amount: remittance?.amount || remittance?.remainingAmount || remittance?.expectedAmount || '',
  paymentDate: getDateTimeInputValue(),
  paymentMethod: remittance?.paymentMethod || 'Bank Transfer',
  bankId: remittance?.bankId || '',
  bankName: remittance?.bankName || '',
  bankCode: remittance?.bankCode || '',
  bankAccountNumber: remittance?.bankAccountNumber || '',
  phoneNumber: remittance?.phoneNumber || '',
  paybillNumber: remittance?.paybillNumber || '',
  paybillAccountNumber: remittance?.paybillAccountNumber || '',
  tillNumber: remittance?.tillNumber || '',
  reference: '',
  notes: ''
});

function PaymentDestinationFields({
  formState,
  setFormState,
  bankOptions,
  loadingBanks,
  bankLoadError,
  onLoadBanks
}) {
  const updateField = (field, value) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const onSelectBank = (event) => {
    const bankId = event.target.value;
    const selectedBank = bankOptions.find((bank) => bank.id === bankId);

    setFormState((current) => ({
      ...current,
      bankId,
      bankName: selectedBank?.name || '',
      bankCode: selectedBank?.code || '',
      bankAccountNumber: current.bankAccountNumber || ''
    }));
  };

  if (formState.paymentMethod === 'Bank Transfer') {
    return (
      <>
        <div className="col-md-6">
          <Form.Label>Bank</Form.Label>
          <Form.Select value={formState.bankId} onChange={onSelectBank}>
            <option value="">Select bank</option>
            {bankOptions.map((bank) => (
              <option key={bank.id} value={bank.id}>
                {bank.name}
              </option>
            ))}
          </Form.Select>
          {loadingBanks && (
            <div className="mt-2 text-muted small">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading Lotus banks...
            </div>
          )}
          {!loadingBanks && bankOptions.length === 0 && (
            <div className="mt-2 small text-muted">
              <Button variant="link" className="p-0 align-baseline" onClick={onLoadBanks}>
                Load bank list from Lotus
              </Button>
            </div>
          )}
          {bankLoadError && <div className="mt-2 small text-danger">{bankLoadError}</div>}
        </div>
        <div className="col-md-6">
          <Form.Label>Bank account number</Form.Label>
          <Form.Control
            value={formState.bankAccountNumber}
            onChange={(event) => updateField('bankAccountNumber', event.target.value)}
            placeholder="Account number"
          />
        </div>
      </>
    );
  }

  if (formState.paymentMethod === 'MPESA') {
    return (
      <div className="col-md-12">
        <Form.Label>MPESA number</Form.Label>
        <Form.Control
          value={formState.phoneNumber}
          onChange={(event) => updateField('phoneNumber', event.target.value)}
          placeholder="e.g. 07XXXXXXXX"
        />
      </div>
    );
  }

  if (formState.paymentMethod === 'Paybill') {
    return (
      <>
        <div className="col-md-6">
          <Form.Label>Paybill number</Form.Label>
          <Form.Control
            value={formState.paybillNumber}
            onChange={(event) => updateField('paybillNumber', event.target.value)}
            placeholder="Paybill number"
          />
        </div>
        <div className="col-md-6">
          <Form.Label>Account number</Form.Label>
          <Form.Control
            value={formState.paybillAccountNumber}
            onChange={(event) => updateField('paybillAccountNumber', event.target.value)}
            placeholder="Account number"
          />
        </div>
      </>
    );
  }

  if (formState.paymentMethod === 'Till Number') {
    return (
      <div className="col-md-12">
        <Form.Label>Till number</Form.Label>
        <Form.Control
          value={formState.tillNumber}
          onChange={(event) => updateField('tillNumber', event.target.value)}
          placeholder="Till number"
        />
      </div>
    );
  }

  return null;
}

function StatutoryRemittancesComp() {
  const auth = getAuth();

  const [loading, setLoading] = useState(true);
  const [remittances, setRemittances] = useState([]);
  const [payments, setPayments] = useState([]);
  const [savingRemittance, setSavingRemittance] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [remittanceModalShow, setRemittanceModalShow] = useState(false);
  const [paymentModalShow, setPaymentModalShow] = useState(false);
  const [formMode, setFormMode] = useState('add');
  const [selectedRemittance, setSelectedRemittance] = useState(null);
  const [remittanceForm, setRemittanceForm] = useState(buildEmptyRemittanceForm());
  const [paymentForm, setPaymentForm] = useState(buildEmptyPaymentForm());
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [bankOptions, setBankOptions] = useState([]);
  const [banksLoaded, setBanksLoaded] = useState(false);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [bankLoadError, setBankLoadError] = useState('');

  const mapLotusBankOptions = useCallback((payload) => {
    const rawBanks = payload?.data?.banks || payload?.data?.bank_list || payload?.banks || [];

    if (!Array.isArray(rawBanks)) {
      return [];
    }

    return rawBanks
      .map((bank) => {
        const bankId = normalizeText(bank?.id || bank?.bank_id || bank?.bankId || bank?.code || bank?.bank_code);
        const bankName = normalizeText(
          bank?.name || bank?.bank_name || bank?.bankName || bank?.display_name || bank?.label || bankId || 'Bank'
        );

        return {
          id: bankId || bankName,
          name: bankName,
          code: normalizeText(bank?.code || bank?.bank_code || bank?.bankCode || ''),
          raw: bank
        };
      })
      .filter((bank) => bank.id || bank.name);
  }, []);

  const fetchLotusBanks = useCallback(async () => {
    if (banksLoaded || loadingBanks) {
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setBankLoadError('Sign in again to load bank options.');
      return;
    }

    setLoadingBanks(true);
    setBankLoadError('');

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${LOTUS_API_BASE_URL}/banks`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || 'Failed to load banks.');
      }

      setBankOptions(mapLotusBankOptions(payload));
      setBanksLoaded(true);
    } catch (error) {
      console.error('Failed to load Lotus banks:', error);
      setBankLoadError('Banks could not be loaded right now.');
    } finally {
      setLoadingBanks(false);
    }
  }, [auth, banksLoaded, loadingBanks, mapLotusBankOptions]);

  const loadPage = useCallback(async () => {
    const [remittanceSnapshot, paymentSnapshot] = await Promise.all([
      getDocs(collection(db, 'StatutoryRemittances')),
      getDocs(collection(db, 'StatutoryRemittancePayments'))
    ]);

    setRemittances(remittanceSnapshot.docs.map((itemDoc) => ({
      id: itemDoc.id,
      ...itemDoc.data()
    })));
    setPayments(paymentSnapshot.docs.map((itemDoc) => ({
      id: itemDoc.id,
      ...itemDoc.data()
    })));
  }, []);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadPage();
      } catch (error) {
        console.error('Failed to load statutory remittances:', error);
        swal('Error', 'Statutory remittances could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [loadPage]);

  useEffect(() => {
    if (
      (remittanceModalShow && remittanceForm.paymentMethod === 'Bank Transfer')
      || (paymentModalShow && paymentForm.paymentMethod === 'Bank Transfer')
    ) {
      fetchLotusBanks();
    }
  }, [
    fetchLotusBanks,
    paymentForm.paymentMethod,
    paymentModalShow,
    remittanceForm.paymentMethod,
    remittanceModalShow
  ]);

  const remittancePaymentsById = useMemo(() => {
    return payments.reduce((accumulator, payment) => {
      const remittanceId = normalizeText(payment.remittanceId);
      if (!remittanceId) {
        return accumulator;
      }

      if (!accumulator[remittanceId]) {
        accumulator[remittanceId] = [];
      }

      accumulator[remittanceId].push(payment);
      return accumulator;
    }, {});
  }, [payments]);

  const remittanceRows = useMemo(() => {
    return remittances.map((remittance) => {
      const remittancePayments = [...(remittancePaymentsById[remittance.id] || [])]
        .sort((first, second) => {
          const firstTime = toDateValue(first.paymentDate)?.getTime() || 0;
          const secondTime = toDateValue(second.paymentDate)?.getTime() || 0;
          return secondTime - firstTime;
        });

      const successfulPayments = remittancePayments.filter((payment) => {
        const status = normalizePaymentStatus(payment.paymentStatus || payment.status);
        return ['paid', 'processed', 'completed', 'successful', 'success', 'succeeded', 'approved'].includes(status);
      });

      const expectedAmount = normalizeAmount(remittance.amount);
      const paidAmount = successfulPayments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
      const remainingAmount = Math.max(expectedAmount - paidAmount, 0);
      const dueDateValue = toDateValue(remittance.dueDate);
      const overdue = Boolean(dueDateValue) && remainingAmount > 0 && dueDateValue.getTime() < Date.now();
      const paymentStatus = expectedAmount <= 0
        ? 'Amount not set'
        : remainingAmount <= 0
          ? 'Paid'
          : paidAmount > 0
            ? 'Partially paid'
            : overdue
              ? 'Overdue'
              : 'Pending';

      return {
        ...remittance,
        expectedAmount,
        paidAmount,
        remainingAmount,
        overdue,
        paymentStatus,
        paymentTone: getStatusTone(paymentStatus),
        paymentCount: remittancePayments.length,
        successfulPaymentCount: successfulPayments.length,
        lastPaymentDate: successfulPayments[0]?.paymentDate || null
      };
    }).sort((first, second) => compareMonthKeys(second.periodKey, first.periodKey));
  }, [remittancePaymentsById, remittances]);

  const summary = useMemo(() => {
    return remittanceRows.reduce((accumulator, row) => {
      accumulator.expected += row.expectedAmount;
      accumulator.paid += row.paidAmount;
      accumulator.remaining += row.remainingAmount;

      if (row.paymentStatus === 'Paid') {
        accumulator.paidCount += 1;
      } else if (row.overdue) {
        accumulator.overdueCount += 1;
      } else {
        accumulator.pendingCount += 1;
      }

      return accumulator;
    }, {
      expected: 0,
      paid: 0,
      remaining: 0,
      paidCount: 0,
      overdueCount: 0,
      pendingCount: 0
    });
  }, [remittanceRows]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return remittanceRows.filter((row) => {
      const matchesType = typeFilter === 'All' || row.remittanceType === typeFilter;
      const matchesStatus = statusFilter === 'All' || normalizePaymentStatus(row.paymentStatus) === normalizePaymentStatus(statusFilter);
      if (!matchesType || !matchesStatus) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchBlob = [
        row.remittanceType,
        row.periodKey,
        getMonthLabel(row.periodKey),
        row.recipientName,
        row.authorityReference,
        row.paymentMethod
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchBlob.includes(normalizedSearch);
    });
  }, [remittanceRows, searchTerm, statusFilter, typeFilter]);

  const resetRemittanceForm = useCallback((defaults = {}) => {
    setRemittanceForm(buildEmptyRemittanceForm(defaults));
  }, []);

  const openCreateModal = () => {
    setFormMode('add');
    setSelectedRemittance(null);
    resetRemittanceForm({
      remittanceType: 'PAYE',
      paymentMethod: 'Bank Transfer',
      recipientName: getDefaultRecipientName('PAYE')
    });
    setRemittanceModalShow(true);
  };

  const openEditModal = (remittance) => {
    setFormMode('edit');
    setSelectedRemittance(remittance);
    resetRemittanceForm({
      remittanceType: remittance.remittanceType || 'PAYE',
      periodKey: remittance.periodKey || getMonthKey(),
      amount: remittance.amount || '',
      dueDate: remittance.dueDate ? getDateInputValue(remittance.dueDate) : getDateInputValue(),
      paymentMethod: remittance.paymentMethod || 'Bank Transfer',
      recipientName: remittance.recipientName || getDefaultRecipientName(remittance.remittanceType || 'PAYE'),
      authorityReference: remittance.authorityReference || '',
      bankId: remittance.bankId || '',
      bankName: remittance.bankName || '',
      bankCode: remittance.bankCode || '',
      bankAccountNumber: remittance.bankAccountNumber || '',
      phoneNumber: remittance.phoneNumber || '',
      paybillNumber: remittance.paybillNumber || '',
      paybillAccountNumber: remittance.paybillAccountNumber || '',
      tillNumber: remittance.tillNumber || '',
      notes: remittance.notes || ''
    });
    setRemittanceModalShow(true);
  };

  const closeRemittanceModal = () => {
    if (savingRemittance) {
      return;
    }

    setRemittanceModalShow(false);
  };

  const openPaymentModal = (remittance) => {
    setSelectedRemittance(remittance);
    setPaymentForm(buildEmptyPaymentForm({
      ...remittance,
      amount: remittance.remainingAmount > 0 ? remittance.remainingAmount : remittance.expectedAmount
    }));
    setPaymentModalShow(true);
  };

  const closePaymentModal = () => {
    if (submittingPayment) {
      return;
    }

    setPaymentModalShow(false);
  };

  const handleRemittanceTypeChange = (event) => {
    const nextType = event.target.value;
    setRemittanceForm((current) => ({
      ...current,
      remittanceType: nextType,
      recipientName: current.recipientName || getDefaultRecipientName(nextType)
    }));
  };

  const handlePaymentMethodChange = (event, setFormState) => {
    const nextMethod = event.target.value;
    setFormState((current) => ({
      ...current,
      paymentMethod: nextMethod,
      bankId: nextMethod === 'Bank Transfer' ? current.bankId : '',
      bankName: nextMethod === 'Bank Transfer' ? current.bankName : '',
      bankCode: nextMethod === 'Bank Transfer' ? current.bankCode : '',
      bankAccountNumber: nextMethod === 'Bank Transfer' ? current.bankAccountNumber : '',
      phoneNumber: nextMethod === 'MPESA' ? current.phoneNumber : '',
      paybillNumber: nextMethod === 'Paybill' ? current.paybillNumber : '',
      paybillAccountNumber: nextMethod === 'Paybill' ? current.paybillAccountNumber : '',
      tillNumber: nextMethod === 'Till Number' ? current.tillNumber : ''
    }));
  };

  const submitRemittance = async () => {
    const amountValue = Number(remittanceForm.amount);
    const dueDateValue = remittanceForm.dueDate ? new Date(remittanceForm.dueDate) : null;

    if (!remittanceForm.remittanceType) {
      swal('Missing type', 'Choose a statutory remittance type.', 'warning');
      return;
    }

    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      swal('Invalid amount', 'Please enter a valid amount.', 'warning');
      return;
    }

    if (!dueDateValue || Number.isNaN(dueDateValue.getTime())) {
      swal('Invalid due date', 'Please choose a valid due date.', 'warning');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      swal('Session expired', 'Please sign in again before saving this remittance.', 'warning');
      return;
    }

    const paymentMethod = normalizeText(remittanceForm.paymentMethod || 'Bank Transfer');
    const lotusPaymentMethod = PAYMENT_METHOD_TO_LOTUS[paymentMethod];
    if (!lotusPaymentMethod) {
      swal('Unsupported method', 'Please choose a supported payment method for Lotus.', 'warning');
      return;
    }

    if (paymentMethod === 'Bank Transfer' && (!normalizeText(remittanceForm.bankId) || !normalizeText(remittanceForm.bankAccountNumber))) {
      swal('Missing bank details', 'Select a bank and enter the account number.', 'warning');
      return;
    }

    if (paymentMethod === 'MPESA' && !normalizeText(remittanceForm.phoneNumber)) {
      swal('Missing phone number', 'Enter the MPESA number before saving.', 'warning');
      return;
    }

    if (paymentMethod === 'Paybill' && (!normalizeText(remittanceForm.paybillNumber) || !normalizeText(remittanceForm.paybillAccountNumber))) {
      swal('Missing paybill details', 'Enter the paybill number and account number.', 'warning');
      return;
    }

    if (paymentMethod === 'Till Number' && !normalizeText(remittanceForm.tillNumber)) {
      swal('Missing till number', 'Enter the till number before saving.', 'warning');
      return;
    }

    setSavingRemittance(true);

    try {
      const remittanceRef = formMode === 'edit' && selectedRemittance
        ? doc(db, 'StatutoryRemittances', selectedRemittance.id)
        : doc(collection(db, 'StatutoryRemittances'));

      const selectedBank = bankOptions.find((bank) => bank.id === remittanceForm.bankId);
      const createdAtValue = formMode === 'edit' && selectedRemittance?.createdAt
        ? selectedRemittance.createdAt
        : Timestamp.now();
      const updatedAtValue = Timestamp.now();
      const remittancePayload = {
        remittanceType: remittanceForm.remittanceType,
        periodKey: remittanceForm.periodKey || getMonthKey(dueDateValue),
        periodLabel: getMonthLabel(remittanceForm.periodKey || getMonthKey(dueDateValue)),
        amount: amountValue,
        dueDate: Timestamp.fromDate(dueDateValue),
        paymentMethod,
        recipientName: normalizeText(remittanceForm.recipientName) || getDefaultRecipientName(remittanceForm.remittanceType),
        authorityReference: normalizeText(remittanceForm.authorityReference),
        bankId: paymentMethod === 'Bank Transfer' ? normalizeText(remittanceForm.bankId) : '',
        bankName: paymentMethod === 'Bank Transfer' ? normalizeText(remittanceForm.bankName || selectedBank?.name) : '',
        bankCode: paymentMethod === 'Bank Transfer' ? normalizeText(remittanceForm.bankCode || selectedBank?.code) : '',
        bankAccountNumber: paymentMethod === 'Bank Transfer' ? normalizeText(remittanceForm.bankAccountNumber) : '',
        phoneNumber: paymentMethod === 'MPESA' ? normalizeText(remittanceForm.phoneNumber) : '',
        paybillNumber: paymentMethod === 'Paybill' ? normalizeText(remittanceForm.paybillNumber) : '',
        paybillAccountNumber: paymentMethod === 'Paybill' ? normalizeText(remittanceForm.paybillAccountNumber) : '',
        tillNumber: paymentMethod === 'Till Number' ? normalizeText(remittanceForm.tillNumber) : '',
        notes: normalizeText(remittanceForm.notes),
        status: formMode === 'edit' && selectedRemittance?.status ? selectedRemittance.status : 'Pending',
        paymentStatus: formMode === 'edit' && selectedRemittance?.paymentStatus ? selectedRemittance.paymentStatus : 'Pending',
        createdAt: createdAtValue,
        updatedAt: updatedAtValue,
        createdById: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email || 'Finance',
        createdByEmail: currentUser.email || '',
        sourcePage: 'statutory-remittances',
        remittanceTag: formMode === 'edit' && selectedRemittance?.remittanceTag
          ? selectedRemittance.remittanceTag
          : `statutory-${remittanceForm.remittanceType}-${remittanceForm.periodKey || getMonthKey(dueDateValue)}-${Date.now()}`
      };

      await setDoc(remittanceRef, remittancePayload, { merge: true });

      const createdRemittance = { id: remittanceRef.id, ...remittancePayload };

      setRemittances((currentRemittances) => (
        formMode === 'edit'
          ? currentRemittances.map((item) => (item.id === remittanceRef.id ? { ...item, ...createdRemittance } : item))
          : [createdRemittance, ...currentRemittances]
      ));

      logAdminActivity({
        action: formMode === 'edit' ? 'Updated statutory remittance' : 'Created statutory remittance',
        details: `${remittancePayload.remittanceType} for ${remittancePayload.periodLabel}.`,
        meta: {
          targetCollection: 'StatutoryRemittances',
          targetId: remittanceRef.id,
          remittanceType: remittancePayload.remittanceType,
          periodKey: remittancePayload.periodKey
        }
      });

      swal('Saved', formMode === 'edit' ? 'Remittance updated successfully.' : 'Remittance added successfully.', 'success');
      setRemittanceModalShow(false);
      setSelectedRemittance(null);
      resetRemittanceForm();
    } catch (error) {
      console.error('Failed to save statutory remittance:', error);
      swal('Error', error.message || 'The remittance could not be saved right now.', 'error');
    } finally {
      setSavingRemittance(false);
    }
  };

  const submitPayment = async () => {
    const selectedAmount = Number(paymentForm.amount);
    const paymentDateValue = paymentForm.paymentDate ? new Date(paymentForm.paymentDate) : null;

    if (!selectedRemittance) {
      swal('Missing remittance', 'Choose a remittance first.', 'warning');
      return;
    }

    if (!Number.isFinite(selectedAmount) || selectedAmount <= 0) {
      swal('Invalid amount', 'Payment amount should be greater than zero.', 'warning');
      return;
    }

    if (selectedAmount > selectedRemittance.remainingAmount) {
      swal(
        'Amount too high',
        `You can only pay up to ${formatKes(selectedRemittance.remainingAmount)} for this remittance.`,
        'warning'
      );
      return;
    }

    if (!paymentDateValue || Number.isNaN(paymentDateValue.getTime())) {
      swal('Invalid date', 'Choose a valid payment date.', 'warning');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      swal('Session expired', 'Please sign in again before submitting this payment.', 'warning');
      return;
    }

    const paymentMethod = normalizeText(paymentForm.paymentMethod || selectedRemittance.paymentMethod || 'Bank Transfer');
    const lotusPaymentMethod = PAYMENT_METHOD_TO_LOTUS[paymentMethod];
    if (!lotusPaymentMethod) {
      swal('Unsupported method', 'Please choose a payment method Lotus can process.', 'warning');
      return;
    }

    if (paymentMethod === 'Bank Transfer' && (!normalizeText(paymentForm.bankId) || !normalizeText(paymentForm.bankAccountNumber))) {
      swal('Missing bank details', 'Select the bank and add the account number.', 'warning');
      return;
    }

    if (paymentMethod === 'MPESA' && !normalizeText(paymentForm.phoneNumber)) {
      swal('Missing phone number', 'Add the MPESA phone number.', 'warning');
      return;
    }

    if (paymentMethod === 'Paybill' && (!normalizeText(paymentForm.paybillNumber) || !normalizeText(paymentForm.paybillAccountNumber))) {
      swal('Missing paybill details', 'Add the paybill number and account number.', 'warning');
      return;
    }

    if (paymentMethod === 'Till Number' && !normalizeText(paymentForm.tillNumber)) {
      swal('Missing till number', 'Add the till number.', 'warning');
      return;
    }

    setSubmittingPayment(true);

    try {
      const idToken = await currentUser.getIdToken();
      const paymentTag = `statutory-${selectedRemittance.remittanceType}-${selectedRemittance.periodKey || getMonthKey()}-${Date.now()}`;
      const paymentRef = doc(collection(db, 'StatutoryRemittancePayments'));
      const selectedBank = bankOptions.find((bank) => bank.id === paymentForm.bankId);
      const lotusPayload = {
        amount: selectedAmount,
        payment_method: lotusPaymentMethod,
        recipient_name: normalizeText(selectedRemittance.recipientName) || getDefaultRecipientName(selectedRemittance.remittanceType),
        payment_purpose: `Statutory remittance for ${selectedRemittance.remittanceType} - ${selectedRemittance.periodLabel || getMonthLabel(selectedRemittance.periodKey)}${normalizeText(selectedRemittance.authorityReference) ? ` | Ref ${normalizeText(selectedRemittance.authorityReference)}` : ''}`,
        payment_tag: paymentTag,
        payment_category: 'statutory_remittance',
        receipt_email: currentUser.email || ''
      };

      if (lotusPaymentMethod === 'pesalink') {
        lotusPayload.bank_id = normalizeText(paymentForm.bankId);
        lotusPayload.bank_account_number = normalizeText(paymentForm.bankAccountNumber);
      }

      if (lotusPaymentMethod === 'mpesa') {
        lotusPayload.phone_number = normalizeText(paymentForm.phoneNumber);
      }

      if (lotusPaymentMethod === 'paybill') {
        lotusPayload.paybill_number = normalizeText(paymentForm.paybillNumber);
        lotusPayload.paybill_account_number = normalizeText(paymentForm.paybillAccountNumber);
      }

      if (lotusPaymentMethod === 'till') {
        lotusPayload.till_number = normalizeText(paymentForm.tillNumber);
      }

      const lotusResponse = await fetch(`${LOTUS_API_BASE_URL}/payments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(lotusPayload)
      });

      const lotusResponseBody = await lotusResponse.json().catch(() => null);
      if (!lotusResponse.ok) {
        const lotusErrorMessage = lotusResponseBody?.error?.message
          || lotusResponseBody?.error?.detail
          || lotusResponseBody?.error
          || lotusResponseBody?.message
          || 'Lotus rejected the payment request.';
        throw new Error(typeof lotusErrorMessage === 'string' ? lotusErrorMessage : 'Lotus rejected the payment request.');
      }

      const lotusPaymentDetails = extractLotusPaymentDetails(lotusResponseBody);
      const lotusPaymentData = lotusResponseBody?.data?.transaction
        || lotusResponseBody?.data
        || lotusResponseBody
        || {};
      const lotusTransactionId = lotusPaymentDetails.lotusTransactionId;
      const lotusTransactionStatus = lotusPaymentDetails.lotusTransactionStatus || 'pending';
      const lotusTransactionMessage = lotusPaymentDetails.lotusTransactionMessage
        || lotusResponseBody?.message
        || 'Payment submitted to Lotus.';

      const paymentPayload = {
        remittanceId: selectedRemittance.id,
        remittanceType: selectedRemittance.remittanceType,
        remittanceLabel: `${selectedRemittance.remittanceType} - ${selectedRemittance.periodLabel || getMonthLabel(selectedRemittance.periodKey)}`,
        periodKey: selectedRemittance.periodKey,
        periodLabel: selectedRemittance.periodLabel || getMonthLabel(selectedRemittance.periodKey),
        amount: selectedAmount,
        paymentDate: Timestamp.fromDate(paymentDateValue),
        paymentMethod,
        reference: normalizeText(paymentForm.reference),
        notes: normalizeText(paymentForm.notes),
        paymentStatus: 'Pending',
        status: 'Pending',
        createdAt: Timestamp.now(),
        createdById: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email || 'Finance',
        createdByEmail: currentUser.email || '',
        docID: paymentRef.id,
        transactionGroupId: paymentTag,
        lotusPaymentTag: paymentTag,
        lotusPaymentMethod,
        lotusTransactionId,
        lotusTransactionStatus,
        lotusTransactionMessage,
        lotusPaymentResponse: lotusPaymentData,
        paymentDestinationSummary: lotusPaymentMethod === 'pesalink'
          ? {
              method: paymentMethod,
              bankId: normalizeText(paymentForm.bankId),
              bankName: normalizeText(paymentForm.bankName || selectedBank?.name),
              bankCode: normalizeText(paymentForm.bankCode || selectedBank?.code),
              bankAccountLast4: normalizeText(paymentForm.bankAccountNumber).slice(-4)
            }
          : lotusPaymentMethod === 'mpesa'
            ? {
                method: paymentMethod,
                phoneLast4: normalizeText(paymentForm.phoneNumber).slice(-4)
              }
            : lotusPaymentMethod === 'paybill'
              ? {
                  method: paymentMethod,
                  paybillNumber: normalizeText(paymentForm.paybillNumber),
                  accountLast4: normalizeText(paymentForm.paybillAccountNumber).slice(-4)
                }
              : {
                  method: paymentMethod,
                  tillNumber: normalizeText(paymentForm.tillNumber)
                },
        sourcePage: 'statutory-remittances'
      };

      await setDoc(paymentRef, paymentPayload);
      const createdPayment = { id: paymentRef.id, ...paymentPayload };

      setPayments((currentPayments) => [createdPayment, ...currentPayments]);
      setPaymentModalShow(false);
      setSelectedRemittance(null);
      setPaymentForm(buildEmptyPaymentForm());

      logAdminActivity({
        action: 'Recorded statutory remittance payment',
        details: `Submitted ${formatKes(selectedAmount)} for ${selectedRemittance.remittanceType} to Lotus.`,
        meta: {
          targetCollection: 'StatutoryRemittancePayments',
          targetId: paymentRef.id,
          remittanceId: selectedRemittance.id,
          lotusTransactionId,
          paymentTransactionGroupId: paymentTag
        }
      });

      swal(
        'Payment submitted',
        `Submitted ${formatKes(selectedAmount)} to Lotus and saved it as pending while the transfer processes.`,
        'success'
      );
    } catch (error) {
      console.error('Failed to submit statutory remittance payment:', error);
      swal('Error', error.message || 'The payment could not be submitted to Lotus.', 'error');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const deleteRemittance = async (remittance) => {
    if (remittance.successfulPaymentCount > 0) {
      swal('Locked record', 'This remittance already has successful payments. Keep it for ledger history.', 'warning');
      return;
    }

    const confirm = await swal({
      title: 'Delete remittance?',
      text: 'This will remove the remittance record permanently.',
      icon: 'warning',
      buttons: true,
      dangerMode: true
    });

    if (!confirm) {
      return;
    }

    await deleteDoc(doc(db, 'StatutoryRemittances', remittance.id));
    setRemittances((currentRemittances) => currentRemittances.filter((item) => item.id !== remittance.id));
    swal('Deleted', 'Remittance removed successfully.', 'success');
  };

  useEffect(() => {
    let cancelled = false;

    const reconcilePendingPayments = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser || payments.length === 0) {
        return;
      }

      const pendingPayments = payments.filter((payment) => {
        const status = normalizePaymentStatus(payment.paymentStatus || payment.status);
        return Boolean(payment.lotusTransactionId) && ['pending', 'processing', 'submitted'].includes(status);
      });

      if (pendingPayments.length === 0) {
        return;
      }

      try {
        const idToken = await currentUser.getIdToken();
        const reconciledAt = Timestamp.now();

        await Promise.all(pendingPayments.map(async (payment) => {
          const response = await fetch(`${LOTUS_API_BASE_URL}/payments/${payment.lotusTransactionId}`, {
            headers: {
              Authorization: `Bearer ${idToken}`
            }
          });

          const payload = await response.json().catch(() => null);
          if (!response.ok) {
            return;
          }

          const lotusPaymentData = payload?.data?.transaction || payload?.data || payload || {};
          const lotusPaymentDetails = extractLotusPaymentDetails(payload);
          const lotusStatusRaw = lotusPaymentDetails.lotusTransactionStatus;
          const paymentStatus = isSuccessfulLotusStatus(lotusStatusRaw)
            ? 'Paid'
            : isFailedLotusStatus(lotusStatusRaw)
              ? 'Failed'
              : null;

          if (!paymentStatus) {
            return;
          }

          if (normalizePaymentStatus(payment.paymentStatus || payment.status) === normalizePaymentStatus(paymentStatus)) {
            return;
          }

          const paymentRef = doc(db, 'StatutoryRemittancePayments', payment.id);
          await setDoc(paymentRef, {
            paymentStatus,
            status: paymentStatus,
            lotusTransactionStatus: lotusStatusRaw || paymentStatus,
            lotusPaymentResponse: lotusPaymentData,
            lotusReconciledAt: reconciledAt,
            lotusReconciledSource: 'polling'
          }, { merge: true });

          const remittanceRef = doc(db, 'StatutoryRemittances', payment.remittanceId);
          await setDoc(remittanceRef, {
            latestPaymentStatus: paymentStatus,
            lotusTransactionStatus: lotusStatusRaw || paymentStatus,
            lotusPaymentResponse: lotusPaymentData,
            lotusReconciledAt: reconciledAt,
            lotusReconciledSource: 'polling'
          }, { merge: true });

          if (!cancelled) {
            setPayments((currentPayments) => currentPayments.map((item) => (
              item.id === payment.id
                ? {
                    ...item,
                    paymentStatus,
                    status: paymentStatus,
                    lotusTransactionStatus: lotusStatusRaw || paymentStatus,
                    lotusPaymentResponse: lotusPaymentData,
                    lotusReconciledAt: reconciledAt,
                    lotusReconciledSource: 'polling'
                  }
                : item
            )));
          }
        }));
      } catch (error) {
        console.error('Failed to reconcile Lotus statutory remittance payments:', error);
      }
    };

    reconcilePendingPayments();
    const intervalId = window.setInterval(reconcilePendingPayments, LOTUS_RECONCILE_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [auth, payments]);

  return (
    <div className="statutoryRemittancesPage">
      <header className="statutoryHeader">
        <div>
          <span className="statutoryEyebrow">Finance / Compliance</span>
          <h1>Statutory remittances</h1>
          <p>Track PAYE, NSSF, SHA, and other obligations and submit payments through Lotus.</p>
        </div>
        <button type="button" className="statutoryAddButton" onClick={openCreateModal}>
          <FontAwesomeIcon icon={faPlus} /> Add remittance
        </button>
      </header>

      <section className="statutoryOverview" aria-label="Statutory remittance summary">
        <div className="statutoryMetric">
          <span>Expected total</span>
          <strong>{formatKes(summary.expected)}</strong>
          <small>{remittanceRows.length} records</small>
        </div>
        <div className="statutoryMetric">
          <span>Outstanding</span>
          <strong>{formatKes(summary.remaining)}</strong>
          <small>{summary.pendingCount} unpaid records</small>
        </div>
        <div className="statutoryMetric">
          <span>Paid records</span>
          <strong>{summary.paidCount}</strong>
          <small>{formatKes(summary.paid)} remitted</small>
        </div>
        <div className="statutoryMetric">
          <span>Overdue</span>
          <strong>{summary.overdueCount}</strong>
          <small>Require attention</small>
        </div>
      </section>

      <section className="statutoryRegister">
        <div className="statutoryRegisterHeader">
          <div>
            <h2>Remittance register</h2>
            <p>Review balances and manage each payment.</p>
          </div>
          <div className="statutoryFilters">
            <Form.Control
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search remittances"
              aria-label="Search remittances"
            />
            <Form.Select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} aria-label="Filter by type">
              <option value="All">All types</option>
              {STATUTORY_TYPE_OPTIONS.map((type) => <option key={type} value={type}>{type}</option>)}
            </Form.Select>
            <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Filter by status">
              <option value="All">All statuses</option>
              <option value="Pending">Pending</option>
              <option value="Partially paid">Partially paid</option>
              <option value="Paid">Paid</option>
              <option value="Overdue">Overdue</option>
            </Form.Select>
          </div>
        </div>
        <div className="statutoryTableWrap">
          {loading ? (
            <div className="statutoryState">
              <Spinner animation="border" role="status" />
              <span>Loading statutory remittances...</span>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="statutoryEmpty">
              <h3>{remittanceRows.length === 0 ? 'No remittances recorded' : 'No matching remittances'}</h3>
              <p>{remittanceRows.length === 0 ? 'Add a statutory obligation to start tracking its payment.' : 'Try a different search or filter.'}</p>
              {remittanceRows.length === 0 ? (
                <button type="button" className="statutoryAddButton" onClick={openCreateModal}>
                  <FontAwesomeIcon icon={faPlus} /> Add remittance
                </button>
              ) : null}
            </div>
          ) : (
            <Table hover className="statutoryTable">
              <thead>
                <tr>
                  <th>Remittance</th>
                  <th>Month</th>
                  <th>Due date</th>
                  <th>Amount</th>
                  <th>Paid</th>
                  <th>Balance</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="statutoryNameCell">
                        <div>
                          <strong>{row.remittanceType}</strong>
                          <span className="statutorySubtext">
                            {row.recipientName || getDefaultRecipientName(row.remittanceType)}
                            {row.authorityReference ? ` • ${row.authorityReference}` : ''}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>{row.periodLabel || getMonthLabel(row.periodKey)}</td>
                    <td>{formatShortDate(row.dueDate)}</td>
                    <td>{formatKes(row.expectedAmount)}</td>
                    <td>{formatKes(row.paidAmount)}</td>
                    <td>{formatKes(row.remainingAmount)}</td>
                    <td>
                      <div className="statutoryMethodCell">
                        <div>
                          <strong>{row.paymentMethod}</strong>
                          <span>{getDestinationSummary(row)}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`statutoryStatus statutoryStatus--${row.paymentTone}`}>
                        {row.paymentStatus}
                      </span>
                      {row.overdue && row.paymentStatus !== 'Paid' && (
                        <div className="statutoryOverdue">Overdue</div>
                      )}
                    </td>
                    <td>
                      {isPaidPaymentStatus(row.paymentStatus) ? (
                        <div className="statutoryPaidAction">
                          <span className="statutoryStatus statutoryStatus--success">Paid</span>
                          <div>Locked after payment</div>
                          {row.lastPaymentDate ? (
                            <div>Paid {formatShortDate(row.lastPaymentDate)}</div>
                          ) : null}
                        </div>
                      ) : (
                        <div className="statutoryActions">
                          <button type="button" className="statutoryActionButton statutoryActionButton--primary" onClick={() => openPaymentModal(row)}>
                            Pay
                          </button>
                          <button type="button" className="statutoryActionButton" onClick={() => openEditModal(row)}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="statutoryActionButton statutoryActionButton--danger"
                            onClick={() => deleteRemittance(row)}
                            disabled={row.successfulPaymentCount > 0}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </section>

      <Modal show={remittanceModalShow} onHide={closeRemittanceModal} centered size="lg" contentClassName="statutoryModal">
        <Modal.Header closeButton>
          <Modal.Title>{formMode === 'edit' ? 'Edit remittance' : 'Add remittance'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Label>Remittance type</Form.Label>
              <Form.Select value={remittanceForm.remittanceType} onChange={handleRemittanceTypeChange}>
                {STATUTORY_TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-6">
              <Form.Label>Month</Form.Label>
              <Form.Control
                type="month"
                value={remittanceForm.periodKey}
                onChange={(event) => setRemittanceForm((current) => ({ ...current, periodKey: event.target.value }))}
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={remittanceForm.amount}
                onChange={(event) => setRemittanceForm((current) => ({ ...current, amount: event.target.value }))}
                placeholder="Amount due"
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Due date</Form.Label>
              <Form.Control
                type="date"
                value={remittanceForm.dueDate}
                onChange={(event) => setRemittanceForm((current) => ({ ...current, dueDate: event.target.value }))}
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Recipient / authority</Form.Label>
              <Form.Control
                value={remittanceForm.recipientName}
                onChange={(event) => setRemittanceForm((current) => ({ ...current, recipientName: event.target.value }))}
                placeholder="KRA, NSSF, SHA..."
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Authority reference</Form.Label>
              <Form.Control
                value={remittanceForm.authorityReference}
                onChange={(event) => setRemittanceForm((current) => ({ ...current, authorityReference: event.target.value }))}
                placeholder="PRN, reference number, account reference"
              />
            </div>
            <div className="col-md-12">
              <Form.Label>Payment method</Form.Label>
              <Form.Select
                value={remittanceForm.paymentMethod}
                onChange={(event) => handlePaymentMethodChange(event, setRemittanceForm)}
              >
                {PAYMENT_METHOD_OPTIONS.map((method) => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </Form.Select>
            </div>
            <PaymentDestinationFields
              formState={remittanceForm}
              setFormState={setRemittanceForm}
              bankOptions={bankOptions}
              loadingBanks={loadingBanks}
              bankLoadError={bankLoadError}
              onLoadBanks={fetchLotusBanks}
            />
            <div className="col-12">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={remittanceForm.notes}
                onChange={(event) => setRemittanceForm((current) => ({ ...current, notes: event.target.value }))}
                placeholder="Optional notes"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRemittanceModal} disabled={savingRemittance}>
            Close
          </Button>
          <Button variant="dark" onClick={submitRemittance} disabled={savingRemittance}>
            {savingRemittance ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Saving...</span>
              </>
            ) : (
              formMode === 'edit' ? 'Save changes' : 'Save remittance'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={paymentModalShow} onHide={closePaymentModal} centered size="lg" contentClassName="statutoryModal">
        <Modal.Header closeButton>
          <Modal.Title>Record remittance payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRemittance && (
            <div className="mb-3 border rounded-3 p-3 bg-light">
              <div className="d-flex justify-content-between flex-wrap gap-2">
                <div>
                  <strong>{selectedRemittance.remittanceType}</strong>
                  <div className="text-muted small">{selectedRemittance.periodLabel || getMonthLabel(selectedRemittance.periodKey)}</div>
                </div>
                <div className="text-end">
                  <div className="small text-muted">Remaining balance</div>
                  <strong>{formatKes(selectedRemittance.remainingAmount)}</strong>
                </div>
              </div>
            </div>
          )}
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={paymentForm.amount}
                onChange={(event) => setPaymentForm((current) => ({ ...current, amount: event.target.value }))}
                placeholder="Amount to pay"
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Payment date</Form.Label>
              <Form.Control
                type="datetime-local"
                value={paymentForm.paymentDate}
                onChange={(event) => setPaymentForm((current) => ({ ...current, paymentDate: event.target.value }))}
              />
            </div>
            <div className="col-md-12">
              <Form.Label>Payment method</Form.Label>
              <Form.Select
                value={paymentForm.paymentMethod}
                onChange={(event) => handlePaymentMethodChange(event, setPaymentForm)}
              >
                {PAYMENT_METHOD_OPTIONS.map((method) => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </Form.Select>
            </div>
            <PaymentDestinationFields
              formState={paymentForm}
              setFormState={setPaymentForm}
              bankOptions={bankOptions}
              loadingBanks={loadingBanks}
              bankLoadError={bankLoadError}
              onLoadBanks={fetchLotusBanks}
            />
            <div className="col-md-6">
              <Form.Label>Reference</Form.Label>
              <Form.Control
                value={paymentForm.reference}
                onChange={(event) => setPaymentForm((current) => ({ ...current, reference: event.target.value }))}
                placeholder="Optional reference"
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                value={paymentForm.notes}
                onChange={(event) => setPaymentForm((current) => ({ ...current, notes: event.target.value }))}
                placeholder="Optional note"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePaymentModal} disabled={submittingPayment}>
            Close
          </Button>
          <Button variant="dark" onClick={submitPayment} disabled={submittingPayment || !selectedRemittance}>
            {submittingPayment ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Submitting...</span>
              </>
            ) : (
              'Submit to Lotus'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default StatutoryRemittancesComp;
